import type { ReactNode } from "react";
import { Icon, type IconName } from "./Icon";
import { ApiError, NetworkError } from "@/lib/api/errors";

interface EmptyStateProps {
  icon?: IconName;
  title: string;
  hint?: string;
  action?: ReactNode;
}

export function EmptyState({ icon = "search", title, hint, action }: EmptyStateProps) {
  return (
    <div className="state">
      <Icon name={icon} size={40} className="state-icon" />
      <div className="state-title">{title}</div>
      {hint && <p className="state-hint">{hint}</p>}
      {action && <div className="state-actions">{action}</div>}
    </div>
  );
}

/**
 * Xato holati.
 *
 * Server konverti ikki tilda xabar va `allowed_actions` beradi
 * (dev_spec.md §4.3). Interfeys ularni KO'RSATADI: «Xatolik yuz
 * berdi» o'rniga foydalanuvchi nima bo'lganini va nima qilishi
 * mumkinligini biladi.
 */
export function ErrorState({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry?: () => void;
}) {
  if (error instanceof ApiError) {
    const details = error.body.details;
    return (
      <div className="state state-error">
        <Icon name="alert" size={40} className="state-icon" />
        <div className="state-title">{error.body.message}</div>
        {error.body.message_ru && <p className="state-hint">{error.body.message_ru}</p>}
        <p className="state-hint mono">
          {error.code}
          {error.requestId ? ` · ${error.requestId}` : ""}
        </p>
        {details && Object.keys(details).length > 0 && (
          <p className="state-hint mono">
            {Object.entries(details)
              .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
              .join(" · ")}
          </p>
        )}
        {error.allowedActions.length > 0 && (
          <p className="state-hint">
            Mumkin bo'lgan amallar: {error.allowedActions.join(", ")}
          </p>
        )}
        {onRetry && (
          <div className="state-actions">
            <button type="button" className="btn btn-tonal" onClick={onRetry}>
              <Icon name="refresh" size={16} />
              Qayta urinish
            </button>
          </div>
        )}
      </div>
    );
  }

  const isNetwork = error instanceof NetworkError;
  return (
    <div className="state state-error">
      <Icon name="alert" size={40} className="state-icon" />
      <div className="state-title">
        {isNetwork ? "Serverga ulanib bo'lmadi" : "Kutilmagan xato"}
      </div>
      <p className="state-hint">
        {isNetwork
          ? "Tarmoq yoki servis ishlamayapti. Ulanish tiklangach qayta urinib ko'ring."
          : String(error)}
      </p>
      {onRetry && (
        <div className="state-actions">
          <button type="button" className="btn btn-tonal" onClick={onRetry}>
            <Icon name="refresh" size={16} />
            Qayta urinish
          </button>
        </div>
      )}
    </div>
  );
}

/** Jadval yuklanayotgandagi skelet. */
export function TableSkeleton({ rows = 6, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div style={{ padding: "var(--space-4)", display: "grid", gap: "var(--space-3)" }}>
      {Array.from({ length: rows }, (_, row) => (
        <div
          key={row}
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gap: "var(--space-4)",
          }}
        >
          {Array.from({ length: columns }, (_, column) => (
            <div
              key={column}
              className="skeleton"
              style={{ height: 14, opacity: 1 - row * 0.1 }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
